import { BreakpointObserver } from '@angular/cdk/layout';
import {
  OriginConnectionPosition,
  Overlay,
  OverlayConfig,
  OverlayConnectionPosition,
  OverlayRef,
  ScrollDispatcher,
} from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FreelancerBreakpoints } from '@freelancer/ui/breakpoints';
import { Margin } from '@freelancer/ui/margin';
import { IconSize } from '../icon/icon.component';

export enum TooltipPosition {
  TOP_START = 'top-start',
  TOP_CENTER = 'top-center',
  TOP_END = 'top-end',
  BOTTOM_START = 'bottom-start',
  BOTTOM_CENTER = 'bottom-center',
  BOTTOM_END = 'bottom-end',
  START_TOP = 'start-top',
  START_CENTER = 'start-center',
  START_BOTTOM = 'start-bottom',
  END_TOP = 'end-top',
  END_CENTER = 'end-center',
  END_BOTTOM = 'end-bottom',
}

export enum TooltipSize {
  MID = 'mid',
}

// TODO: More features.
// - Animations (T38383)
// - Hide delay (T38384)
// - light color
@Component({
  selector: 'fl-tooltip',
  template: `
    <span class="IconWrapper">
      <fl-bit class="IconContainer IconContent" *ngIf="defaultIcon">
        <fl-icon [name]="'ui-help'" [size]="IconSize.SMALL"></fl-icon>
      </fl-bit>
      <span class="IconContent">
        <ng-content></ng-content>
      </span>
    </span>

    <ng-template cdk-portal>
      <fl-tooltip-content [position]="currentPosition" [size]="size">
        {{ message }}
      </fl-tooltip-content>
    </ng-template>
  `,
  styleUrls: ['./tooltip.component.scss'],
})
export class TooltipComponent implements OnDestroy {
  IconSize = IconSize;
  Margin = Margin;

  @Input() message: string;
  @Input() position: TooltipPosition;
  @Input() positionTablet: TooltipPosition;
  @Input() positionDesktopSmall: TooltipPosition;
  @Input() positionDesktopLarge: TooltipPosition;
  @Input() size = TooltipSize.MID;
  /** Use the default icon provided by this component */
  @Input() defaultIcon = false;

  currentPosition: TooltipPosition = TooltipPosition.BOTTOM_CENTER;
  private overlayRef?: OverlayRef;

  @ViewChild(TemplatePortalDirective, { static: false })
  portal: TemplatePortalDirective;

  constructor(
    private overlay: Overlay,
    private breakpointObserver: BreakpointObserver,
    private elementRef: ElementRef<HTMLElement>,
    private scrollDispatcher: ScrollDispatcher,
  ) {}

  @HostListener('mouseenter')
  onMouseenter() {
    this.show();
  }
  @HostListener('mouseleave')
  onMouseleave() {
    this.hide();
  }

  ngOnDestroy() {
    this.dispose();
  }

  show() {
    if (
      !this.message ||
      !this.message.trim() ||
      this.overlayRef !== undefined
    ) {
      return;
    }

    this.overlayRef = this.createOverlay();
    this.overlayRef.attach(this.portal);
  }

  hide() {
    this.dispose();
  }

  private dispose() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
  }

  private getOverlayPosition(): OverlayConnectionPosition {
    const position = this.currentPosition.split('-');

    const anchor = position[0];
    const relativePosition = position[1];

    if (
      (anchor === 'top' || anchor === 'bottom') &&
      (relativePosition === 'start' ||
        relativePosition === 'center' ||
        relativePosition === 'end')
    ) {
      return {
        overlayX: relativePosition,
        overlayY: anchor === 'top' ? 'bottom' : 'top',
      };
    }

    if (
      (anchor === 'start' || anchor === 'end') &&
      (relativePosition === 'top' ||
        relativePosition === 'center' ||
        relativePosition === 'bottom')
    ) {
      return {
        overlayX: anchor === 'start' ? 'end' : 'start',
        overlayY: relativePosition,
      };
    }

    throw Error('No position for tooltip specified!');
  }

  private getOrigin(): OriginConnectionPosition {
    const position = this.currentPosition.split('-');

    const anchor = position[0];
    const relativePosition = position[1];

    if (
      (anchor === 'top' || anchor === 'bottom') &&
      (relativePosition === 'start' ||
        relativePosition === 'center' ||
        relativePosition === 'end')
    ) {
      return { originX: relativePosition, originY: anchor };
    }

    if (
      (anchor === 'start' || anchor === 'end') &&
      (relativePosition === 'top' ||
        relativePosition === 'center' ||
        relativePosition === 'bottom')
    ) {
      return {
        originX: anchor,
        originY: relativePosition,
      };
    }

    throw Error('No position for tooltip specified!');
  }

  private createOverlay() {
    if (this.position) {
      this.currentPosition = this.position;
    }
    if (
      this.positionTablet &&
      this.breakpointObserver.isMatched(FreelancerBreakpoints.TABLET)
    ) {
      this.currentPosition = this.positionTablet;
    }
    if (
      this.positionDesktopSmall &&
      this.breakpointObserver.isMatched(FreelancerBreakpoints.DESKTOP_SMALL)
    ) {
      this.currentPosition = this.positionDesktopSmall;
    }
    if (
      this.positionDesktopLarge &&
      this.breakpointObserver.isMatched(FreelancerBreakpoints.DESKTOP_LARGE)
    ) {
      this.currentPosition = this.positionDesktopLarge;
    }

    const { originX, originY } = this.getOrigin();
    const { overlayX, overlayY } = this.getOverlayPosition();
    const strategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX,
          originY,
          overlayX,
          overlayY,
        },
      ])
      .withScrollableContainers(
        this.scrollDispatcher.getAncestorScrollContainers(this.elementRef),
      );

    const state = new OverlayConfig();
    state.positionStrategy = strategy;

    return this.overlay.create(state);
  }
}
